import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Skeleton,
  Alert,
  Chip,
  Link,
  Avatar,
  Tooltip,
  Button,
  Collapse,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  OpenInNew as OpenPageIcon,
  Close as ClearIcon,
  Shield as CommanderIcon,
  Business as DirectorIcon,
  HowToReg as HeadcounterIcon,
  SupportAgent as BackupIcon,
} from '@mui/icons-material';
import type { IncidentAssignment, IncidentAssignmentRoleName } from '../../Types/IncidentAssignment';
import type { IncidentRosterPerson } from '../../Types/Incident';
import {
  groupIncidentAssignmentsByRole,
  getAssignmentRoleLabel,
  pluralizeRoleLabel,
  getRoleSubtitle,
  INCIDENT_TEAM_ROLES as ALL_ROLES,
  ECT_ROLE_NAMES,
  getAssignmentRoleColor,
  isProminentEctRole,
  normalizeIncidentAssignments,
} from '../../Utils/incidentTeamHelpers';
import { appColors } from '../../Config/Theme';
import IncidentDayBadgeInfo from '../Elements/IncidentDayBadgeInfo';

/* ── constants ── */

const roleIcon = (role: IncidentAssignmentRoleName, size: number): React.ReactElement => {
  if (role === 'IncidentCommander' || role === 'ECTLead') return <CommanderIcon sx={{ fontSize: size }} />;
  if (role === 'OfficeDirector') return <DirectorIcon sx={{ fontSize: size }} />;
  if (role === 'RecallTeam' || role === 'BackupHeadcounter') return <BackupIcon sx={{ fontSize: size }} />;
  return <HeadcounterIcon sx={{ fontSize: size }} />;
};

const COLLAPSE_THRESHOLD = 12;

const ALWAYS_EXPANDED_ROLES: IncidentAssignmentRoleName[] = [
  'IncidentCommander',
  'OfficeDirector',
  ...ECT_ROLE_NAMES,
];

/* ── helpers ── */

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '?';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const formatCountSummary = (roleCounts: Record<string, number>): string => {
  const parts: string[] = [];
  const ic = roleCounts['IncidentCommander'] ?? 0;
  const od = roleCounts['OfficeDirector'] ?? 0;
  const hc = roleCounts['Headcounter'] ?? 0;
  const recall = roleCounts['RecallTeam'] ?? 0;
  if (ic > 0) parts.push(`${ic} ${ic === 1 ? 'commander' : 'commanders'}`);
  if (od > 0) parts.push(`${od} ${od === 1 ? 'director' : 'directors'}`);
  if (hc > 0) parts.push(`${hc} ${hc === 1 ? 'headcounter' : 'headcounters'}`);
  for (const role of ECT_ROLE_NAMES) {
    const count = roleCounts[role] ?? 0;
    if (count > 0) parts.push(`${count} ${pluralizeRoleLabel(role, count)}`);
  }
  if (recall > 0) parts.push(`${recall} Recall Team`);
  return parts.join(' · ');
};

/* ── props ── */

interface IncidentTeamDrawerProps {
  open: boolean;
  onClose: () => void;
  assignments: IncidentAssignment[];
  loading?: boolean;
  error?: unknown;
  incidentName?: string;
  incidentId?: string;
}

/* ── component ── */

const IncidentTeamDrawer: React.FC<IncidentTeamDrawerProps> = ({
  open,
  onClose,
  assignments,
  loading,
  error,
  incidentName,
  incidentId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<IncidentAssignmentRoleName | null>(null);
  const [badgedInFilter, setBadgedInFilter] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const activeAssignments = useMemo(
    () => normalizeIncidentAssignments(assignments).filter((a) => a.isActive),
    [assignments],
  );

  const roleCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const role of ALL_ROLES) counts[role] = 0;
    for (const a of activeAssignments) {
      counts[a.assignmentRoleName] = (counts[a.assignmentRoleName] ?? 0) + 1;
    }
    return counts;
  }, [activeAssignments]);

  const badgedInCount = useMemo(
    () => activeAssignments.filter((a) => a.hasIncidentDayBadgeActivity === true).length,
    [activeAssignments],
  );

  const filtered = useMemo(() => {
    let result = activeAssignments;
    if (roleFilter) {
      result = result.filter((a) => a.assignmentRoleName === roleFilter);
    }
    if (badgedInFilter) {
      result = result.filter((a) => a.hasIncidentDayBadgeActivity === true);
    }
    if (!searchQuery.trim()) return result;
    const q = searchQuery.toLowerCase();
    return result.filter(
      (a) =>
        a.displayName?.toLowerCase().includes(q) ||
        a.email?.toLowerCase().includes(q) ||
        a.phone?.toLowerCase().includes(q) ||
        getAssignmentRoleLabel(a.assignmentRoleName, a.assignmentRole).toLowerCase().includes(q) ||
        a.assignmentRoleName?.toLowerCase().includes(q) ||
        a.viewType?.toLowerCase().includes(q) ||
        a.viewValue?.toLowerCase().includes(q),
    );
  }, [activeAssignments, roleFilter, badgedInFilter, searchQuery]);

  const groups = useMemo(() => groupIncidentAssignmentsByRole(filtered), [filtered]);

  const isSectionExpanded = (roleName: IncidentAssignmentRoleName): boolean => {
    if (expandedSections[roleName] !== undefined) return expandedSections[roleName];
    if (roleFilter || badgedInFilter || searchQuery.trim()) return true;
    if (ALWAYS_EXPANDED_ROLES.includes(roleName)) return true;
    return activeAssignments.length <= COLLAPSE_THRESHOLD;
  };

  const toggleSection = (roleName: IncidentAssignmentRoleName) => {
    setExpandedSections((prev) => ({ ...prev, [roleName]: !isSectionExpanded(roleName) }));
  };

  React.useEffect(() => {
    setExpandedSections({});
  }, [roleFilter, badgedInFilter, searchQuery]);

  const clearSearch = () => setSearchQuery('');

  const totalCount = activeAssignments.length;
  const drawerWidth = isMobile ? '100%' : 560;
  const summaryText = formatCountSummary(roleCounts);

  const filterChips: { key: string; label: string; count: number; role: IncidentAssignmentRoleName | null }[] = [
    { key: 'all', label: 'All', count: totalCount, role: null },
    ...ALL_ROLES
      .filter((role) => roleCounts[role] > 0)
      .map((role) => ({
        key: role,
        label: role === 'IncidentCommander' ? 'Commanders'
          : role === 'OfficeDirector' ? 'Directors'
          : role === 'Headcounter' ? 'Headcounters'
          : getAssignmentRoleLabel(role),
        count: roleCounts[role],
        role,
      })),
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      aria-labelledby="incident-team-drawer-title"
      slotProps={{
        paper: {
          sx: { width: drawerWidth, maxWidth: '100vw' },
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

        {/* ── Sticky header ── */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            bgcolor: appColors.surface.card,
            borderBottom: `1px solid ${appColors.border.default}`,
          }}
        >
          {/* Title row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              px: 2.5,
              pt: 2,
              pb: 0.75,
            }}
          >
            <Box>
              <Typography
                id="incident-team-drawer-title"
                variant="h6"
              sx={{ fontWeight: 700, color: appColors.text.heading, lineHeight: 1.3 }}
              >
                Incident Team
              </Typography>
              {incidentName && (
                <Typography variant="body2" sx={{ color: appColors.text.caption, mt: 0.15 }}>
                  {incidentName}
                </Typography>
              )}
              {!loading && !error && totalCount > 0 && (
                <Typography variant="caption" sx={{ color: appColors.text.body, mt: 0.15, display: 'block' }}>
                  {summaryText}
                </Typography>
              )}
            </Box>
            <IconButton onClick={onClose} aria-label="Close incident team drawer" size="small" sx={{ mt: 0.25 }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Search */}
          <Box sx={{ px: 2.5, pt: 0.75, pb: 0.75 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search by name, phone, email, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 20, color: appColors.text.placeholder }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={clearSearch} aria-label="Clear search">
                        <ClearIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                  'aria-label': 'Search incident team',
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '24px',
                  bgcolor: appColors.surface.input,
                  '& fieldset': { borderColor: appColors.border.subtle },
                  '&:hover fieldset': { borderColor: appColors.border.hover },
                },
              }}
            />
          </Box>

          {/* Role filter chips */}
          {!loading && !error && totalCount > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, px: 2.5, pb: 1.25, flexWrap: 'wrap' }}>
              {filterChips.map((chip) => {
                const isActive = !badgedInFilter && roleFilter === chip.role;
                return (
                  <Chip
                    key={chip.key}
                    label={`${chip.label} ${chip.count}`}
                    size="small"
                    clickable
                    onClick={() => { setRoleFilter(chip.role); setBadgedInFilter(false); }}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 26,
                      bgcolor: isActive ? appColors.primary.main : appColors.surface.inactive,
                      color: isActive ? '#FFFFFF' : appColors.text.body,
                      '&:hover': { bgcolor: isActive ? appColors.primary.dark : appColors.border.default },
                    }}
                  />
                );
              })}
              {badgedInCount > 0 && (
                <Chip
                  key="badgedIn"
                  label={`Badged In ${badgedInCount}`}
                  size="small"
                  clickable
                  onClick={() => setBadgedInFilter((prev) => !prev)}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: 26,
                    bgcolor: badgedInFilter ? '#2E7D32' : appColors.surface.inactive,
                    color: badgedInFilter ? '#FFFFFF' : appColors.text.body,
                    '&:hover': { bgcolor: badgedInFilter ? '#1B5E20' : appColors.border.default },
                  }}
                />
              )}
            </Box>
          )}
        </Box>

        {/* ── Scrollable content ── */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <>
          {/* Search result count */}
          {searchQuery.trim() && !loading && !error && (
            <Typography variant="caption" sx={{ display: 'block', px: 2.5, pt: 1, color: appColors.text.caption }}>
              Showing {filtered.length} of {roleFilter ? roleCounts[roleFilter] : totalCount} team members
            </Typography>
          )}

          {/* Loading */}
          {loading && (
            <Box sx={{ px: 2.5, pt: 2 }}>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rounded" height={60} sx={{ mb: 1.5, borderRadius: '10px' }} />
              ))}
            </Box>
          )}

          {/* Error */}
          {!loading && error && (
            <Box sx={{ px: 2.5, pt: 2 }}>
              <Alert severity="warning" variant="outlined" sx={{ borderRadius: '10px' }}>
                Incident team could not be loaded.
              </Alert>
            </Box>
          )}

          {/* Empty / no results */}
          {!loading && !error && filtered.length === 0 && (
            <Box sx={{ px: 2.5, pt: 4, textAlign: 'center' }}>
              {searchQuery.trim() ? (
                <>
                  <Typography variant="body2" sx={{ color: appColors.text.placeholder, mb: 1 }}>
                    No team members match &ldquo;{searchQuery}&rdquo;.
                  </Typography>
                  <Button size="small" variant="outlined" onClick={clearSearch}>
                    Clear search
                  </Button>
                </>
              ) : (
                <Typography variant="body2" sx={{ color: appColors.text.placeholder }}>
                  No incident team assigned.
                </Typography>
              )}
            </Box>
          )}

          {/* Role sections */}
          {!loading && !error && groups.map((group) => {
            const expanded = isSectionExpanded(group.roleName);
            return (
              <Box key={group.roleName} sx={{ mx: 1.5, mt: 1, mb: 0.5 }}>
                {/* Section header */}
                <Box
                  role="button"
                  tabIndex={0}
                  aria-expanded={expanded}
                  aria-label={`${pluralizeRoleLabel(group.roleName, group.assignments.length)}, ${group.assignments.length} people`}
                  onClick={() => toggleSection(group.roleName)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleSection(group.roleName);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.5,
                    py: 1,
                    cursor: 'pointer',
                    borderRadius: '10px 10px 0 0',
                    bgcolor: '#FAFBFC',
                    border: `1px solid ${appColors.border.subtle}`,
                    borderBottom: expanded ? `1px solid ${appColors.border.subtle}` : `1px solid ${appColors.border.subtle}`,
                    '&:hover': { bgcolor: '#F5F6F8' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ color: getAssignmentRoleColor(group.roleName), display: 'flex' }}>
                      {roleIcon(group.roleName, 18)}
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: isProminentEctRole(group.roleName) ? 800 : 700, fontSize: isProminentEctRole(group.roleName) ? '1.125rem' : undefined, color: appColors.text.heading, lineHeight: 1.3 }}>
                          {pluralizeRoleLabel(group.roleName, group.assignments.length)}
                        </Typography>
                        <Chip
                          label={`${group.assignments.length}`}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            bgcolor: appColors.surface.inactive,
                            color: appColors.text.body,
                          }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ color: appColors.text.caption, fontSize: '0.65rem', lineHeight: 1.2 }}>
                        {getRoleSubtitle(group.roleName)}
                      </Typography>
                    </Box>
                  </Box>
                  {expanded
                    ? <ExpandLessIcon sx={{ fontSize: 18, color: appColors.text.caption }} />
                    : <ExpandMoreIcon sx={{ fontSize: 18, color: appColors.text.caption }} />
                  }
                </Box>

                {/* Section body */}
                <Collapse in={expanded}>
                  <Box
                    sx={{
                      border: `1px solid ${appColors.border.subtle}`,
                      borderTop: 'none',
                      borderRadius: '0 0 10px 10px',
                      overflow: 'hidden',
                    }}
                  >
                    {group.assignments.map((a, idx) => (
                      <DrawerContactRow
                        key={a.incidentAssignmentId}
                        assignment={a}
                        avatarColor={getAssignmentRoleColor(group.roleName)}
                        isLast={idx === group.assignments.length - 1}
                      />
                    ))}
                  </Box>
                </Collapse>
              </Box>
            );
          })}

          {/* Bottom spacer */}
          <Box sx={{ height: 16 }} />
          </>
        </Box>

        {/* ── Sticky footer ── */}
        {incidentId && (
          <Box
            sx={{
              borderTop: `1px solid ${appColors.border.default}`,
              px: 2.5,
              py: 1.25,
              bgcolor: appColors.surface.card,
            }}
          >
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={<OpenPageIcon sx={{ fontSize: 16 }} />}
              onClick={() => {
                onClose();
                navigate(`/incidents/${incidentId}/team`);
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                borderColor: appColors.border.subtle,
                color: appColors.text.body,
                '&:hover': { borderColor: appColors.primary.main, color: appColors.primary.main },
              }}
            >
              Open Full Team Page
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

/* ── Contact row ── */

interface DrawerContactRowProps {
  assignment: IncidentAssignment;
  avatarColor: string;
  isLast: boolean;
}

const DrawerContactRow: React.FC<DrawerContactRowProps> = ({ assignment: a, avatarColor, isLast }) => {
  const roleLabel = getAssignmentRoleLabel(a.assignmentRoleName, a.assignmentRole);
  const prominent = isProminentEctRole(a.assignmentRoleName, a.assignmentRole);
  const isBadgedIn = a.hasIncidentDayBadgeActivity === true;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.5,
        py: 1,
        borderBottom: isLast ? 'none' : `1px solid ${appColors.border.light}`,
        bgcolor: isBadgedIn ? '#E8F5E9' : undefined,
        '&:hover': { bgcolor: isBadgedIn ? '#C8E6C9' : '#FAFBFC' },
      }}
    >
      {/* Initials avatar */}
      <Avatar
        sx={{
          width: 36,
          height: 36,
          bgcolor: avatarColor,
          fontWeight: 600,
          fontSize: '0.75rem',
          flexShrink: 0,
        }}
        aria-hidden="true"
      >
        {getInitials(a.displayName)}
      </Avatar>

      {/* Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography
            variant="body2"
            noWrap
            title={a.displayName}
            sx={{ fontWeight: prominent ? 800 : 700, color: appColors.text.heading, fontSize: prominent ? '1rem' : '0.875rem' }}
          >
            {a.displayName}
          </Typography>
          <Typography variant="caption" sx={{ color: avatarColor, fontWeight: prominent ? 800 : 600, fontSize: prominent ? '0.875rem' : '0.65rem' }}>
            {roleLabel}
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ color: appColors.text.caption, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.1, fontSize: '0.72rem' }}>
          {a.email && (
            <Link
              href={`mailto:${a.email}`}
              underline="hover"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              sx={{ color: 'inherit', '&:hover': { color: appColors.primary.main } }}
            >
              {a.email}
            </Link>
          )}
          {a.email && a.phone && <Box component="span">·</Box>}
          {a.phone && (
            <Link
              href={`tel:${a.phone}`}
              underline="hover"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              sx={{ color: 'inherit', '&:hover': { color: appColors.primary.main } }}
            >
              {a.phone}
            </Link>
          )}
        </Typography>
        {/* Badge activity */}
        {(a.lastIncidentDayBadgeActivity || a.lobbyTrackActivity || a.hasIncidentDayBadgeActivity !== undefined || a.rosterSource || a.isAssignedToIncidentOffice) && (
          <Box sx={{ mt: 0.25 }}>
            <IncidentDayBadgeInfo person={a as unknown as IncidentRosterPerson} />
          </Box>
        )}
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
        {a.phone && (
          <Tooltip title={`Call ${a.displayName}`} arrow>
            <IconButton
              size="small"
              component="a"
              href={`tel:${a.phone}`}
              aria-label={`Call ${a.displayName} at ${a.phone}`}
              sx={{ color: appColors.primary.main, p: 0.5 }}
            >
              <PhoneIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {a.email && (
          <Tooltip title={`Email ${a.displayName}`} arrow>
            <IconButton
              size="small"
              component="a"
              href={`mailto:${a.email}`}
              aria-label={`Email ${a.displayName}`}
              sx={{ color: appColors.primary.main, p: 0.5 }}
            >
              <EmailIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default IncidentTeamDrawer;
